import { EntityType } from '@frontend/common';
import { getFunctionalityDisplayName } from '@frontend/functionality/utils';
import { useActiveModal, useModals } from '@frontend/modal';
import { useAccountRepository } from '@frontend/repository';
import { SettingClient } from '@frontend/setting/api';
import { Setting } from '@frontend/setting/types';
import { StepClient, WizardClient } from '@frontend/wizard/api';
import { clearWizards, initialiseWizard } from '@frontend/wizard/mobile';
import { ScreenTrigger, ScreenType } from '@frontend/wizard/types';
import type { Step, Wizard } from '@frontend/wizard/types';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import { useAppDispatch, useAppSelector } from '../../redux/store';
import { useWizardTransaction } from '../hooks/useWizardTransaction';
import type { WizardModalHeaderStep } from './wizard-modal-header.component';
import { WizardModalProps } from './wizard-modal.component';

const TERMINAL_SCREENS = new Set<ScreenType>([ScreenType.HOME, ScreenType.PREVIOUS, ScreenType.START_SCREEN]);
const FORWARD_TRIGGERS = new Set<ScreenTrigger>([ScreenTrigger.SUBMIT, ScreenTrigger.SUBMIT_TRUE, ScreenTrigger.SUBMIT_FALSE]);

const getScreenLabel = (screen: ScreenType): string =>
    screen
        .replace(/_/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
        .replace(/\b\w/g, (character) => character.toUpperCase());

const resolveFutureScreens = (steps: Step[], currentScreen: ScreenType): ScreenType[] => {
    const futureScreens: ScreenType[] = [];
    const visited = new Set<ScreenType>([currentScreen]);
    let screen = currentScreen;

    while (true) {
        const nextSteps = steps.filter((step) => step.screen === screen && FORWARD_TRIGGERS.has(step.trigger) && !TERMINAL_SCREENS.has(step.next_screen));
        const uniqueNextScreens = [...new Set(nextSteps.map((step) => step.next_screen))];

        if (uniqueNextScreens.length !== 1) return futureScreens;

        const nextScreen = uniqueNextScreens[0];
        if (visited.has(nextScreen)) return futureScreens;

        futureScreens.push(nextScreen);
        visited.add(nextScreen);
        screen = nextScreen;
    }
};

const resolveHeaderSteps = (steps: Step[], history: ScreenType[], currentScreen: ScreenType): WizardModalHeaderStep[] => {
    const path = [...history, currentScreen, ...resolveFutureScreens(steps, currentScreen)];
    const deduplicatedPath = path.filter((screen, index) => path.indexOf(screen) === index && !TERMINAL_SCREENS.has(screen));
    const currentIndex = deduplicatedPath.indexOf(currentScreen);

    return deduplicatedPath.map((screen, index) => ({
        screen,
        label: getScreenLabel(screen),
        status: index < currentIndex ? 'completed' : index === currentIndex ? 'current' : 'upcoming'
    }));
};

export const useWizardModal = (props: WizardModalProps) => {
    const intl = useIntl();
    const dispatch = useAppDispatch();
    const { currentAccount } = useAccountRepository();
    const modalWizardStore = useAppSelector((state) => state.modal_wizards);
    const { closeModal } = useModals();
    const { modalId } = useActiveModal();
    const [wizard, setWizard] = useState<Wizard | null>(null);
    const [steps, setSteps] = useState<Step[] | null>(null);
    const [functionalitySettings, setFunctionalitySettings] = useState<Setting[] | null>(null);

    const store = wizard ? (modalWizardStore[wizard.id] ?? null) : null;

    const currentScreen = useMemo(() => {
        if (wizard && modalWizardStore[wizard.id]) return modalWizardStore[wizard.id].currentScreen;
        return null;
    }, [modalWizardStore, wizard]);

    const title = useMemo(() => getFunctionalityDisplayName(intl, props.functionality), [intl, props.functionality]);

    const headerSteps = useMemo(() => {
        if (!store || !steps || !currentScreen) return [];
        return resolveHeaderSteps(steps, store.history ?? [], currentScreen);
    }, [currentScreen, steps, store]);

    useEffect(() => {
        dispatch(clearWizards());
    }, [dispatch]);

    useEffect(() => {
        if (props.functionality) {
            SettingClient.fetchSettings({ entity_type: EntityType.FUNCTIONALITY, entity_id: props.functionality.id }).then((res) => {
                setFunctionalitySettings(res.results);
            });
        }

        if (props.wizardId) {
            WizardClient.fetchWizard(currentAccount, props.wizardId).then(setWizard);
            StepClient.fetchSteps({ account_id: currentAccount, wizard_id: props.wizardId }).then((res) => setSteps(res.results));
        }
    }, [props.functionality, props.wizardId, currentAccount]);

    useEffect(() => {
        if (props.functionality && functionalitySettings && wizard && steps) {
            if (!modalWizardStore[wizard.id]) {
                dispatch(
                    initialiseWizard({
                        key: wizard.id,
                        init: {
                            functionality: props.functionality,
                            functionalitySettings,
                            wizard,
                            steps,
                            data: props.initialData
                        }
                    })
                );
            }
        }
    }, [modalWizardStore, props.functionality, functionalitySettings, wizard, steps, dispatch, props.initialData]);

    const { isLoading: isTransactionLoading } = useWizardTransaction({
        wizardId: wizard?.id,
        store,
        autoFetch: true
    });

    const onCancel = useCallback(() => {
        if (modalId) closeModal(modalId);
    }, [closeModal, modalId]);

    const isLoading = !wizard || !steps || !currentScreen || !store || isTransactionLoading;

    return {
        currentScreen,
        headerSteps,
        title,
        onCancel,
        isLoading
    };
};
